/**
 * 场景加载 effect — 管理 RendererController 生命周期与场景加载全流程。
 *
 * 严格顺序依赖（init→fetch→load→bind）保持在单一 effect 中，
 * 不拆成多个独立 effect 以避免竞态。
 */
import { type RefObject, useEffect, useRef } from 'react';
import type { Track } from '@mindcloudxai_3d/core';
import { RendererController } from '@renderer/index';
import { useViewerStore } from '@stores/viewerStore';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import type { ExploreUrlParams } from '@hooks/useUrlParams';

import { NETWORK_OFFLINE_MESSAGE, handleLoadError } from './errors';
import {
  resolveSceneConfig,
  resolveTrack,
  resolveInitialHotspotIndex,
  loadRendererScene,
} from './sceneControl';
import { bindViewerEvents, deriveCinematicState } from './viewerEvents';

const LOAD_COMPLETE_PROGRESS_ANIMATION_MS = 500;

function waitForLoadCompleteAnimation(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const handleAbort = () => {
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, LOAD_COMPLETE_PROGRESS_ANIMATION_MS);
    signal.addEventListener('abort', handleAbort, { once: true });
  });
}

// ---------------------------------------------------------------------------
// Hook 入参
// ---------------------------------------------------------------------------

export interface UseSceneLoaderParams {
  containerRef: RefObject<HTMLDivElement | null>;
  controllerRef: RefObject<RendererController | null>;
  trackRef: RefObject<Track | null>;
  urlParams: ExploreUrlParams;
  retryVersion: number;
  resetRetryCount: () => void;
}

// ---------------------------------------------------------------------------
// effect 与 startSession 共享的可变上下文
// ---------------------------------------------------------------------------

interface SessionContext {
  disposed: boolean;
  offViewerEvents: (() => void) | null;
  offLoadingEvents: (() => void) | null;
}

// ---------------------------------------------------------------------------
// 场景加载全流程（effect 外纯函数）
// ---------------------------------------------------------------------------

interface StartSessionOptions {
  ctrl: RendererController;
  container: HTMLElement;
  ctx: SessionContext;
  cleanupSession: () => void;
  handleOffline: () => void;
  signal: AbortSignal;
  trackRef: RefObject<Track | null>;
  urlParams: ExploreUrlParams;
  resetRetryCount: () => void;
}

/** init → fetch → load → bind events。所有 await 后检查 ctx.disposed 防止竞态。 */
async function runSceneSession(opts: StartSessionOptions): Promise<void> {
  const {
    ctrl,
    container,
    ctx,
    cleanupSession,
    handleOffline,
    signal,
    trackRef,
    urlParams,
    resetRetryCount,
  } = opts;
  const {
    sceneId,
    directUrl,
    hotspotId,
    taskCode,
    taskId,
    collisionUrl,
    debugCollision,
    debugTrackRoute,
    mockTrack,
    trackDuration,
    trackCenter,
    renderer,
    deviceType,
    avatarUrl: paramAvatarUrl,
    ministats,
  } = urlParams;

  const {
    setStatus,
    setSceneId,
    setTaskData,
    setAccessInfo,
    setSceneRole,
    setSceneConfig,
    setLoadProgress,
    setActiveHotspotIndex,
    setNavigationMode,
  } = useViewerStore.getState();

  try {
    if (!navigator.onLine) {
      handleOffline();
      return;
    }

    // ---------- Phase 1.5: 无场景源时跳过加载，等待用户拖拽文件 ----------
    if (!sceneId && !directUrl && !taskCode && !taskId) {
      // 仅初始化 renderer，不进入 loading 状态；idle 不会触发 LoadingOverlay
      const avatarUrl = paramAvatarUrl ?? `${import.meta.env.BASE_URL}avatar/character.glb`;
      const enableDefaultAvatar = deviceType !== 'webgl';
      await ctrl.init({
        container,
        renderer,
        deviceType,
        avatarUrl,
        avatarEnabled: paramAvatarUrl ? true : enableDefaultAvatar,
        ministats,
      });
      if (ctx.disposed) return;
      // 绑定 viewer 事件，使后续文件加载时 navigationMode/anim 等状态同步到 store
      ctx.offViewerEvents = bindViewerEvents(ctrl, trackRef);
      setStatus('ready');
      return;
    }

    setStatus('loading');
    setLoadProgress(0);

    // ---------- Phase 1: 初始化 renderer ----------
    const avatarUrl = paramAvatarUrl ?? `${import.meta.env.BASE_URL}avatar/character.glb`;
    const enableDefaultAvatar = deviceType !== 'webgl';
    await ctrl.init({
      container,
      renderer,
      deviceType,
      avatarUrl,
      avatarEnabled: paramAvatarUrl ? true : enableDefaultAvatar,
      ministats,
    });
    // ctx.disposed 检查在下方 resolveSceneConfig await 之后统一处理

    // viewer 的 ready 事件来自 firstFrame，代表 splat 真正渲染出首帧；
    // loadScene resolve 仅代表 gsplat 实体已创建，此时尚未渲染，过早结束 loading
    // 会暴露初始化背景色（#0f172a），表现为一段蓝屏。这里在 init 后即注册监听，
    // 避免错过 firstFrame；progress 事件用于让加载进度条反映真实下载进度。
    let resolveFirstFrame!: () => void;
    const waitForFirstFrame = new Promise<void>((resolve) => {
      resolveFirstFrame = resolve;
    });
    const onReady = () => resolveFirstFrame();
    // 字节下载进度封顶 99%：下载完成后仍有 GPU 解码+首帧渲染，
    // 100% 留给 firstFrame，避免「进度满格却还在解码」的卡满观感。
    // 同步遵守单调递增，防止 frame:ready 回退覆盖已有进度
    const onProgress = (p: number) => {
      const current = useViewerStore.getState().loadProgress ?? 0;
      if (p > current) setLoadProgress(Math.min(p, 99));
    };
    ctrl.on('ready', onReady);
    ctrl.on('progress', onProgress);
    ctx.offLoadingEvents = () => {
      ctrl.off('ready', onReady);
      ctrl.off('progress', onProgress);
    };

    // ---------- Phase 2: 获取场景数据 + 构建 config ----------
    const { config, baseRotation, task, access } = await resolveSceneConfig({
      sceneId,
      directUrl,
      taskCode,
      taskId,
      signal,
    });
    if (ctx.disposed) return;
    if (!task && !config) return;

    if (task) {
      setSceneId(sceneId ?? null);
      setTaskData(task);
      if (access) {
        setAccessInfo(access);
        setSceneRole(access.role);
      }
    }

    setSceneConfig(config);
    setActiveHotspotIndex(resolveInitialHotspotIndex(config, hotspotId));

    // mock 轨迹优先于后端 track 数据
    const track = resolveTrack(mockTrack, trackDuration, trackCenter, config.track);
    trackRef.current = track;
    ctx.offViewerEvents = bindViewerEvents(ctrl, trackRef);

    // ---------- Phase 3: 加载场景 ----------
    await loadRendererScene(ctrl, {
      config,
      track,
      collisionUrl,
      debugCollision,
      debugTrackRoute,
      baseRotation,
    });
    if (ctx.disposed) return;

    // 等首帧真正渲染出 splat 后再结束 loading，避免 loadScene resolve 与首帧之间的蓝屏。
    await waitForFirstFrame;
    if (ctx.disposed) return;

    resetRetryCount();
    setLoadProgress(100);
    await waitForLoadCompleteAnimation(signal);
    // 最后一处 await 后统一检查 disposed
    if (ctx.disposed) return;
    setStatus('ready');

    // 加载完成即解绑 ready/progress 监听，避免后续 FileDropZone 拖拽加载时
    // 此处无单调守卫的 onProgress 覆盖 FileDropZone 的模拟进度
    ctx.offLoadingEvents?.();
    ctx.offLoadingEvents = null;

    // ---------- Phase 4: 同步首帧后的 viewer 状态 ----------
    if (track) {
      setNavigationMode(ctrl.navigation.getMode());
      const animState = ctrl.navigation.anim.getState();
      const s = useViewerStore.getState();
      s.setCinematicCurrentTime(animState.time);
      s.setCinematicState(deriveCinematicState(animState));
    } else {
      ctrl.navigation.setMode('orbit');
      setNavigationMode(ctrl.navigation.getMode());
    }
  } catch (err) {
    if (!ctx.disposed) {
      handleLoadError(err, setStatus, setLoadProgress);
      // 权限/未找到等致命错误时释放完整会话，避免错误页继续持有 renderer 运行时监听。
      cleanupSession();
    }
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * 主加载 effect hook。
 * 返回 cleanupSessionRef 供外部（如鉴权撤权 hook）中断正在进行的会话。
 */
export function useSceneLoader(params: UseSceneLoaderParams): RefObject<(() => void) | null> {
  const { containerRef, controllerRef, trackRef, urlParams, retryVersion, resetRetryCount } =
    params;

  const cleanupSessionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const container: HTMLElement = containerRef.current!;
    if (!container) return;

    const { setStatus, setLoadProgress, reset } = useViewerStore.getState();

    const ctx: SessionContext = { disposed: false, offViewerEvents: null, offLoadingEvents: null };
    let cleaned = false;
    const abortController = new AbortController();

    trackRef.current = null;
    controllerRef.current?.dispose();

    const ctrl = new RendererController();
    controllerRef.current = ctrl;

    reset();

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      ctrl.resize(width, height);
    });
    ro.observe(container);

    let cleanupSession: () => void = () => undefined;
    const handleOffline = () => {
      cleanupSession();
      resetRetryCount();
      setLoadProgress(null);
      setStatus('error', NETWORK_OFFLINE_MESSAGE);
    };

    cleanupSession = () => {
      if (cleaned) return;
      cleaned = true;
      ctx.disposed = true;
      abortController.abort();
      ro.disconnect();
      window.removeEventListener('offline', handleOffline);
      trackRef.current = null;
      ctx.offLoadingEvents?.();
      ctx.offViewerEvents?.();
      // 场景切换时清空已导入模型，避免旧模型残留到新场景
      useAssetLibraryStore.getState().clearAssets();
      ctrl.dispose();
      if (controllerRef.current === ctrl) controllerRef.current = null;
    };

    cleanupSessionRef.current = cleanupSession;
    window.addEventListener('offline', handleOffline);

    void runSceneSession({
      ctrl,
      container,
      ctx,
      cleanupSession,
      handleOffline,
      signal: abortController.signal,
      trackRef,
      urlParams,
      resetRetryCount,
    });

    return () => {
      // 先中断请求，再释放运行时，避免旧异步污染新会话。
      cleanupSession();
      if (cleanupSessionRef.current === cleanupSession) cleanupSessionRef.current = null;
    };
    // URL 参数字段变化时重建会话；K12 场景不依赖登录态，无需 auth 门控。
  }, [containerRef, controllerRef, trackRef, urlParams, retryVersion, resetRetryCount]);

  return cleanupSessionRef;
}
